import { ArrowRight, ClipboardPaste, Link2, X } from 'lucide-react';
import { useId, type ClipboardEvent, type FormEvent, type Ref } from 'react';
import { parseYouTubeVideoId } from '../../shared/youtube-url';
import { canReadClipboard } from '../lib/clipboard';

export interface UrlFormProps {
  value: string;
  onChange: (value: string) => void;
  /** Called with a valid video ID (typed + Enter, pasted, or read from the clipboard). */
  onVideo: (videoId: string) => void;
  /** Called with the raw text when it contains no YouTube link. */
  onInvalid: (text: string) => void;
  onPasteButton: () => void;
  hint: { tone: 'error' | 'info'; text: string } | null;
  compact?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export function UrlForm({ value, onChange, onVideo, onInvalid, onPasteButton, hint, compact = false, ref }: UrlFormProps) {
  const hintId = useId();
  const inputId = useId();
  const showPaste = !value && canReadClipboard();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const text = value.trim();
    if (!text) {
      onInvalid('');
      return;
    }
    const id = parseYouTubeVideoId(text);
    if (id) onVideo(id);
    else onInvalid(text);
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text/plain');
    const id = parseYouTubeVideoId(text);
    const replacesAll =
      !value || (event.currentTarget.selectionStart === 0 && event.currentTarget.selectionEnd === value.length);
    if (id && replacesAll) {
      event.preventDefault();
      onChange(text.trim());
      onVideo(id);
    } else if (!id && replacesAll && text.trim()) {
      // Let the paste happen, then explain.
      window.setTimeout(() => onInvalid(text.trim()), 0);
    }
  };

  return (
    <form
      className={`url-form${compact ? ' url-form--compact' : ''}`}
      onSubmit={submit}
      role="search"
      aria-label="YouTube-Video zusammenfassen"
      noValidate
    >
      <div className={`url-form__field glass${hint?.tone === 'error' ? ' has-error' : ''}`}>
        <Link2 className="url-form__icon" size={compact ? 18 : 22} aria-hidden="true" />
        <label htmlFor={inputId} className="sr-only">
          YouTube-Link
        </label>
        <input
          ref={ref}
          id={inputId}
          className="url-form__input"
          type="text"
          inputMode="url"
          enterKeyHint="go"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder={compact ? 'Weiteren Link einfügen' : 'YouTube-Link einfügen'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onPaste={onPaste}
          aria-invalid={hint?.tone === 'error' ? true : undefined}
          aria-describedby={hint ? hintId : undefined}
        />
        {value ? (
          <button type="button" className="icon-btn icon-btn--subtle url-form__clear" onClick={() => onChange('')} aria-label="Eingabe löschen">
            <X size={18} aria-hidden="true" />
          </button>
        ) : (
          showPaste && (
            <button
              type="button"
              className="btn btn--ghost url-form__paste"
              onClick={onPasteButton}
              aria-label="Link aus der Zwischenablage einfügen"
            >
              <ClipboardPaste size={18} aria-hidden="true" />
              <span>Einfügen</span>
            </button>
          )
        )}
        <button type="submit" className="btn btn--primary url-form__submit" aria-label="Zusammenfassen">
          <span className="url-form__submit-label">Zusammenfassen</span>
          <ArrowRight size={20} aria-hidden="true" />
        </button>
      </div>
      <p id={hintId} className={`url-form__hint${hint ? ` is-${hint.tone}` : ''}`} aria-live="polite">
        {hint?.text}
      </p>
    </form>
  );
}
